import React, { useEffect, useMemo, useState } from 'react';
import {
  Paper,
  Typography,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  InputLabel,
  Chip,
  Switch,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Visibility as ViewIcon } from '@mui/icons-material';
import axios from 'axios';

interface PaymentMode {
  id: string;
  name: string;
  code: string;
  isOnline: boolean;
  requiresReference: boolean;
  isActive: boolean;
}

const PaymentModeMaster: React.FC = () => {
  const [items, setItems] = useState<PaymentMode[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<PaymentMode | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    isOnline: false,
    requiresReference: false,
    isActive: true,
  });
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get('/system-settings/payment-mode');
      setItems(data || []);
    } catch (error) {
      console.error('Failed to fetch payment modes:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = useMemo(() => {
    if (!search) return items;
    const lowerSearch = search.toLowerCase();
    return items.filter(
      (item) =>
        item.name.toLowerCase().includes(lowerSearch) ||
        item.code.toLowerCase().includes(lowerSearch)
    );
  }, [items, search]);

  const handleCreate = () => {
    setEditingItem(null);
    setFormData({
      name: '',
      code: '',
      isOnline: false,
      requiresReference: false,
      isActive: true,
    });
    setDialogOpen(true);
  };

  const handleEdit = (item: PaymentMode) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      code: item.code,
      isOnline: item.isOnline,
      requiresReference: item.requiresReference,
      isActive: item.isActive,
    });
    setDialogOpen(true);
  };

  const handleToggleStatus = async (id: string, currentStatus: boolean) => {
    try {
      await axios.patch(`/system-settings/payment-mode/${id}/toggle-status`);
      setItems((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, isActive: !currentStatus } : item
        )
      );
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to toggle status');
      fetchItems();
    }
  };

  const handleSave = async () => {
    try {
      const payload = {
        name: formData.name.trim(),
        code: formData.code.trim(),
        isOnline: formData.isOnline,
        requiresReference: formData.requiresReference,
        isActive: formData.isActive,
      };

      if (editingItem) {
        await axios.patch(`/system-settings/payment-mode/${editingItem.id}`, payload);
      } else {
        await axios.post('/system-settings/payment-mode', payload);
      }
      setDialogOpen(false);
      await fetchItems();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to save payment mode');
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this payment mode?')) {
      try {
        await axios.delete(`/system-settings/payment-mode/${id}`);
        await fetchItems();
      } catch (error: any) {
        alert(error.response?.data?.message || 'Cannot delete payment mode. It may be in use.');
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Payment Mode</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
          Create
        </Button>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <TextField
          label="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ width: 300 }}
        />
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Payment Mode Name</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Online</TableCell>
              <TableCell>Reference Required</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No payment modes found
                </TableCell>
              </TableRow>
            ) : (
              filteredItems.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>{item.code}</TableCell>
                  <TableCell>
                    <Chip
                      label={item.isOnline ? 'Yes' : 'No'}
                      color={item.isOnline ? 'primary' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={item.requiresReference ? 'Yes' : 'No'}
                      color={item.requiresReference ? 'secondary' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={item.isActive}
                      onChange={() => handleToggleStatus(item.id, item.isActive)}
                      color="primary"
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      title="View"
                      onClick={() =>
                        alert(
                          `Name: ${item.name}\nCode: ${item.code}\nOnline: ${
                            item.isOnline ? 'Yes' : 'No'
                          }\nRef Required: ${item.requiresReference ? 'Yes' : 'No'}\nStatus: ${
                            item.isActive ? 'Active' : 'Inactive'
                          }`
                        )
                      }
                    >
                      <ViewIcon />
                    </IconButton>
                    <IconButton size="small" title="Edit" onClick={() => handleEdit(item)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" title="Delete" onClick={() => handleDelete(item.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? 'Edit Payment Mode' : 'Create Payment Mode'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Payment Mode Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Code"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
              fullWidth
              required
              helperText="Unique code for this payment mode (e.g., CASH, UPI)"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isOnline}
                  onChange={(e) => setFormData({ ...formData, isOnline: e.target.checked })}
                />
              }
              label="Is Online Payment?"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.requiresReference}
                  onChange={(e) => setFormData({ ...formData, requiresReference: e.target.checked })}
                />
              }
              label="Requires Reference Number?"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={!formData.name || !formData.code}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentModeMaster;
